/**
 * Owner: Masa Bayu
 * File: publisher-signup.ts
 * Date:Time: 2026-10-21 20:15
 * Description: §452 — klien daftar akaun penerbit pada qxp-hub. Hanya satu
 *   panggilan nyata: POST {hub}/v1/accounts/register dengan role "publisher"
 *   (gerbang peranan §423). Token sesi TIDAK disimpan di sini — skrin ini
 *   menyerahkan log masuk kepada panel akaun QXEditor.
 */

import { qxpHubOrigin } from '../../publication/hub-sync';

/** Cerminan hub-domain.mjs: EMAIL_PATTERN + MIN_PASSWORD_LENGTH (hub-store.mjs:31). */
export const HUB_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const HUB_MIN_PASSWORD_LENGTH = 8;

export type PublisherSignupError =
  | 'email-taken'
  | 'invalid'
  | 'unavailable'
  | 'bad-credentials'
  | 'forbidden';

export type PublisherSignupOutcome =
  | { readonly ok: true; readonly accountId: string }
  | { readonly ok: false; readonly error: PublisherSignupError };

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export function validatePublisherDetails(
  email: string,
  password: string,
): PublisherSignupError | null {
  if (!HUB_EMAIL_PATTERN.test(email.trim().toLowerCase())) return 'invalid';
  if (password.length < HUB_MIN_PASSWORD_LENGTH) return 'invalid';
  return null;
}

export async function registerPublisherAccount(
  email: string,
  password: string,
  fetchImpl: FetchLike = fetch,
  origin: string = qxpHubOrigin(),
): Promise<PublisherSignupOutcome> {
  const mail = email.trim().toLowerCase();
  const invalid = validatePublisherDetails(mail, password);
  if (invalid !== null) return { ok: false, error: invalid };
  let res: Response;
  try {
    res = await fetchImpl(`${origin}/v1/accounts/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: mail, password, role: 'publisher' }),
    });
  } catch {
    return { ok: false, error: 'unavailable' };
  }
  if (res.status === 409) return { ok: false, error: 'email-taken' };
  if (res.status === 422) return { ok: false, error: 'invalid' };
  if (!res.ok) return { ok: false, error: 'unavailable' };
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, error: 'unavailable' };
  }
  const accountId =
    typeof body === 'object' && body !== null && 'accountId' in body
      ? Reflect.get(body, 'accountId')
      : undefined;
  if (typeof accountId !== 'string' || accountId.length === 0) {
    return { ok: false, error: 'unavailable' };
  }
  return { ok: true, accountId };
}

/**
 * Rekod tempatan BUKAN rahsia (tiada token): ID penerbit yang pernah didaftar
 * pada peranti ini. Skrin ini membacanya supaya pengunjung yang sudah berdaftar
 * melihat paparan siap + pintu QXEditor, bukan borang kosong dua kali.
 */
const PUBLISHER_ID_KEY = 'qxread.publisher.account.v1';

export function storePublisherAccountId(accountId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PUBLISHER_ID_KEY, accountId);
}

export function readPublisherAccountId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const value = localStorage.getItem(PUBLISHER_ID_KEY);
  return typeof value === 'string' && value.length > 0 ? value : null;
}
